import { isPlatformBrowser } from '@angular/common';
import {
    Directive,
    effect,
    inject,
    input,
    OnDestroy,
    PLATFORM_ID,
} from '@angular/core';
import { environment } from '../../environments/environment';

@Directive({
    selector: '[dynamicHeaderPage]',
    standalone: true,
})
export class DynamicHeaderPageDirective implements OnDestroy {
    pageDataLoaded = input.required<boolean>();
    dynamicHeaderPage = input.required<boolean>();

    excludedHeaders = input<string[]>([]);

    private readonly platformId = inject(PLATFORM_ID);
    private readonly isBrowser = isPlatformBrowser(this.platformId);

    private boundScrollHandler?: () => void;
    private boundResizeHandler?: () => void;
    private boundVisibilityHandler?: () => void;
    private boundPageShowHandler?: () => void;
    private boundFocusHandler?: () => void;
    private resizeTimeout?: number;
    private initTimeout?: number;
    private initFrame?: number;

    private readonly maxInitAttempts = 10;
    private readonly legacyHeaderSelector = [
        'lib-babylon-header-book-show',
        'lib-babylon-header-clear',
        'lib-babylon-header-menu-center',
        'lib-babylon-header-menu-show',
        'lib-babylon-header-menu-logo-center',
    ].join(',');

    constructor() {
        if (!this.isBrowser) return;

        // Efecto reactivo
        effect(() => {
            if (!this.pageDataLoaded()) return;
            this.scheduleStickyInitialization();
        });

        // Handlers de resize
        this.boundResizeHandler = () => {
            if (this.resizeTimeout) window.clearTimeout(this.resizeTimeout);
            this.resizeTimeout = window.setTimeout(() => {
                this.resizeTimeout = undefined;
                this.scheduleStickyInitialization();
            }, 250);
        };

        this.boundVisibilityHandler = () => {
            if (document.visibilityState === 'visible') {
                this.restoreHeaderAfterBrowserPause();
            }
        };

        this.boundPageShowHandler = () => {
            this.restoreHeaderAfterBrowserPause();
        };

        this.boundFocusHandler = () => {
            this.restoreHeaderAfterBrowserPause();
        };

        window.addEventListener('resize', this.boundResizeHandler);
        window.addEventListener('mouseup', this.boundResizeHandler);
        window.addEventListener('pageshow', this.boundPageShowHandler);
        window.addEventListener('focus', this.boundFocusHandler);
        document.addEventListener(
            'visibilitychange',
            this.boundVisibilityHandler
        );
    }

    ngOnDestroy(): void {
        if (!this.isBrowser) return;

        if (this.boundResizeHandler) {
            window.removeEventListener('resize', this.boundResizeHandler);
            window.removeEventListener('mouseup', this.boundResizeHandler);
        }

        if (this.boundPageShowHandler) {
            window.removeEventListener('pageshow', this.boundPageShowHandler);
        }

        if (this.boundFocusHandler) {
            window.removeEventListener('focus', this.boundFocusHandler);
        }

        if (this.boundVisibilityHandler) {
            document.removeEventListener(
                'visibilitychange',
                this.boundVisibilityHandler
            );
        }

        if (this.boundScrollHandler) {
            window.removeEventListener('scroll', this.boundScrollHandler);
        }

        if (this.resizeTimeout) {
            window.clearTimeout(this.resizeTimeout);
        }

        this.clearPendingInitialization();
    }

    // -------------------------------------------------------
    // 🔧 Inicialización principal
    // -------------------------------------------------------
    private scheduleStickyInitialization(attempt = 0): void {
        if (!this.isBrowser) return;

        this.clearPendingInitialization();

        const delay = attempt === 0 ? 0 : Math.min(100 * attempt, 500);
        this.initTimeout = window.setTimeout(() => {
            this.initTimeout = undefined;
            this.initFrame = window.requestAnimationFrame(() => {
                this.initFrame = undefined;

                const initialized = this.initializeStickyFeatures();
                if (
                    !initialized &&
                    this.pageDataLoaded() &&
                    attempt < this.maxInitAttempts
                ) {
                    this.scheduleStickyInitialization(attempt + 1);
                }
            });
        }, delay);
    }

    private initializeStickyFeatures(): boolean {
        if (!this.isBrowser) return false;

        const excludedList = this.excludedHeaders();

        const isExcluded = excludedList.some(
            (selector) => !!document.querySelector(selector)
        );

        if (isExcluded) {
            document.body.classList.remove('header_sticky');
            this.revealHeaderContainers();
            return true;
        }

        if (this.shouldWaitForLegacyHeader()) {
            return false;
        }

        const engineEnabled = environment.booking_engine_enabled;
        const headerSelectors = engineEnabled
            ? ['#headerBookShow', '#headerCenterBookShow']
            : ['#headerCenterBookShow'];

        const validator = headerSelectors.some((sel) =>
            document.querySelector(sel)
        );

        const hasTopSlider =
            !!document.querySelector('lib-babylon-top-slider') ||
            !!document.querySelector('lib-babylon-top-slider-thumbs') ||
            !!document.querySelector('lib-babylon-top-slider-video');

        this.revealHeaderContainers();
        this.applyHeaderClasses();

        if (this.dynamicHeaderPage()) {
            this.initScrollMode(validator, hasTopSlider);
        } else {
            this.initStaticMode(validator);
        }

        return true;
    }

    private clearPendingInitialization(): void {
        if (this.initTimeout) {
            window.clearTimeout(this.initTimeout);
            this.initTimeout = undefined;
        }

        if (this.initFrame) {
            window.cancelAnimationFrame(this.initFrame);
            this.initFrame = undefined;
        }
    }

    private restoreHeaderAfterBrowserPause(): void {
        if (!this.pageDataLoaded()) return;

        this.forceHeaderReflow();
        this.scheduleStickyInitialization();
    }

    private shouldWaitForLegacyHeader(): boolean {
        const legacyHeader = document.querySelector(this.legacyHeaderSelector);
        if (!legacyHeader) return false;

        return !legacyHeader.querySelector('.fixed_header');
    }

    private revealHeaderContainers(): void {
        document
            .querySelectorAll(
                '.header-desktop-container, .header-mobile-container'
            )
            .forEach((container) => {
                (container as HTMLElement).style.opacity = '1';
            });
    }

    private forceHeaderReflow(): void {
        const header = document.querySelector(
            '.header-desktop-container, .header-mobile-container'
        ) as HTMLElement | null;

        if (header) {
            void header.offsetHeight;
        }
    }

    // -------------------------------------------------------
    // 📌 Modo dinámico (con scroll)
    // -------------------------------------------------------
    private initScrollMode(validator: boolean, hasTopSlider: boolean): void {
        this.revealHeaderContainers();

        if (this.boundScrollHandler) {
            window.removeEventListener('scroll', this.boundScrollHandler);
        }

        this.boundScrollHandler = this.manageScroll.bind(
            this,
            validator,
            hasTopSlider
        );
        window.addEventListener('scroll', this.boundScrollHandler);

        window.dispatchEvent(new Event('scroll'));
    }

    // -------------------------------------------------------
    // 📌 Modo estático (sin scroll dependiente)
    // -------------------------------------------------------
    private initStaticMode(validator: boolean): void {
        if (this.boundScrollHandler) {
            window.removeEventListener('scroll', this.boundScrollHandler);
        }

        document.body.classList.add('header_sticky');

        document
            .querySelectorAll('.fixed_header')
            ?.forEach((el) =>
                el.classList.add('header-sticky', 'cs_gescout_sticky')
            );

        if (validator) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('engine-sticky');
            this.moveEngineTo('.babylon__header__box');
        }

        this.revealHeaderContainers();
    }

    // -------------------------------------------------------
    // ♻ Scroll principal
    // -------------------------------------------------------
    private manageScroll(validator: boolean, hasTopSlider: boolean): void {
        if (!this.isBrowser) return;

        if (window.scrollY > 100) {
            document
                .querySelectorAll('.fixed_header')
                ?.forEach((el) =>
                    el.classList.add('header-sticky', 'cs_gescout_sticky')
                );

            if (validator) {
                document
                    .querySelector('.babylon__engine')
                    ?.classList.add('engine-sticky');
                this.moveEngineTo('.babylon__header__box');
            }

            document.body.classList.add('header_sticky');
            hasTopSlider
                ? document.body.classList.add('has_topslider')
                : document.body.classList.remove('has_topslider');
        } else {
            document
                .querySelectorAll('.fixed_header')
                ?.forEach((el) =>
                    el.classList.remove('header-sticky', 'cs_gescout_sticky')
                );

            if (validator) {
                document
                    .querySelector('.babylon__engine')
                    ?.classList.remove('engine-sticky');
                this.moveEngineTo('.top-container');
            }

            document.body.classList.remove('header_sticky');
            document.body.classList.remove('has_topslider');
        }

        // Animación de gallery
        if (window.innerWidth >= 1024) {
            const el = document.getElementById('babylon__gallery-slider');
            const position = (el?.offsetTop ?? 200) - 200;
            const gallery = document.querySelector(
                '.babylon__gallery-slider .babylon__gallery-slider--content'
            );

            if (!gallery) return;

            window.scrollY >= position
                ? gallery.classList.remove('no-active')
                : gallery.classList.add('no-active');
        }
    }

    // -------------------------------------------------------
    // 🔧 Helpers seguros para el engine
    // -------------------------------------------------------
    private moveEngineTo(targetSelector: string): void {
        const engine = document.querySelector('lib-babylon-engine');
        const target = document.querySelector(targetSelector);

        // Elementos no existen
        if (!engine || !target) return;

        // ❌ NO mover si es una hydration shell vacía
        if (engine.childNodes.length === 0 && engine.innerHTML.trim() === '') {
            return;
        }

        // ❌ NO mover si no es un nodo válido
        if (!(engine instanceof Node) || engine.nodeType !== 1) {
            return;
        }

        try {
            target.appendChild(engine);
        } catch (err) {
            console.warn('No se pudo mover lib-babylon-engine:', err);
        }
    }

    private applyHeaderClasses(): void {
        if (document.querySelector('#headerCenterBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-center-bookshow-engine');
        }

        if (document.querySelector('#headerBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-bookshow-engine');
        }
    }
}
